// One task timer, at a time (design_handoff_tasks_module, "Timers").
//
// "One timer at a time — starting a second stops the first and says so."
// "restored on reload."
//
// So the running timer is not component state: it outlives the panel, the
// route and the tab. It lives in one localStorage record, and everything else
// reads it. Elapsed time is derived from the start stamp rather than counted,
// because a counter stops when the tab is backgrounded and a stamp does not.
//
// What this does NOT yet do is write the timesheet entry on stop. The handoff
// requires it, and the timesheet save path is the one with the rev guard, so it
// is deliberately a separate step (phase 5) rather than a half-write here. The
// elapsed seconds are banked on the task in the meantime, so nothing is lost.

const KEY = 'track-task-timer';

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (_) { return null; }
};
const write = (v) => {
  try { v ? localStorage.setItem(KEY, JSON.stringify(v)) : localStorage.removeItem(KEY); } catch (_) { /* private mode */ }
};

/** The running timer, or null. `now` is injectable so this is testable. */
export function running(now = Date.now()) {
  const r = read();
  if (!r || !r.taskId || !r.startedAt) return null;
  return { taskId: r.taskId, startedAt: r.startedAt, elapsed: Math.max(0, Math.floor((now - r.startedAt) / 1000)) };
}

/**
 * Start a timer on `taskId`. If another is running it is STOPPED first and
 * returned, so the caller can bank its seconds and say what happened — silently
 * dropping the first timer's time is the one thing this must never do.
 * @returns {{stopped: null|{taskId,seconds}}}
 */
export function start(taskId, now = Date.now()) {
  const prev = running(now);
  let stopped = null;
  if (prev) {
    if (prev.taskId === taskId) return { stopped: null, alreadyRunning: true };
    stopped = { taskId: prev.taskId, seconds: prev.elapsed };
  }
  write({ taskId, startedAt: now });
  return { stopped };
}

/** Stop the running timer and report what it measured. */
export function stop(now = Date.now()) {
  const r = running(now);
  write(null);
  return r ? { taskId: r.taskId, seconds: r.elapsed } : null;
}

/** Stop only if this task is the one running (the row's own stop button). */
export function stopIf(taskId, now = Date.now()) {
  const r = running(now);
  if (!r || r.taskId !== taskId) return null;
  return stop(now);
}

export const isRunning = (taskId, now = Date.now()) => running(now)?.taskId === taskId;
